import {
  Attachment,
  AttachmentFile,
  GuestAttachmentMap,
} from "@/types/Attachment";
import { PortalConfig } from "@/types/PortalConfig";
import { ConditionGroup } from "@/types/EventRule";
import { EmailPreflightResult } from "@/types/AttachmentDrop";
import {
  getAttachments as getAttachmentsApi,
  getAttachment as getAttachmentApi,
  createAttachment as createAttachmentApi,
  updateAttachment as updateAttachmentApi,
  deleteAttachment as deleteAttachmentApi,
  bulkDeleteAttachments as bulkDeleteAttachmentsApi,
  presignFileUpload as presignFileUploadApi,
  confirmFileUpload as confirmFileUploadApi,
  deleteFile as deleteFileApi,
  getDownloadUrl as getDownloadUrlApi,
  resolveForGuests as resolveForGuestsApi,
  getGuestAttachments as getGuestAttachmentsApi,
  emailPreflight as emailPreflightApi,
  generatePortalLink as generatePortalLinkApi,
  getPortalConfig as getPortalConfigApi,
  updatePortalConfig as updatePortalConfigApi,
  listPortalTokens as listPortalTokensApi,
  revokePortalToken as revokePortalTokenApi,
  revokeAllPortalTokens as revokeAllPortalTokensApi,
  portalTokenSource,
  type PortalTokenSummary,
  type PortalTokenSource,
  type GeneratedPortalLink,
} from "@/lib/api/clients/AttachmentClient";
export type { PortalTokenSummary, PortalTokenSource, GeneratedPortalLink };
export { portalTokenSource };
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

// ── Attachment Definition CRUD ──

export async function getAttachments(
  eventId: string,
): Promise<Attachment[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await getAttachmentsApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getAttachment(
  eventId: string,
  attachmentId: string,
): Promise<Attachment | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(attachmentId)) {
      throw new Error("Invalid ID.");
    }
    return await getAttachmentApi(eventId, attachmentId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createAttachment(
  eventId: string,
  data: {
    name: string;
    assignmentType: string;
    sourceType: string;
    accessType: string;
    targetType: string;
    filters?: { key: string; operator: string; value: string | string[] }[];
    condition?: ConditionGroup;
    documentBuilderTemplateIds?: string[];
    includeCompanionCopies?: boolean;
    sortOrder?: number;
  },
): Promise<Attachment | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await createAttachmentApi(eventId, data);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateAttachment(
  eventId: string,
  attachmentId: string,
  data: {
    name?: string;
    assignmentType?: string;
    sourceType?: string;
    accessType?: string;
    targetType?: string;
    filters?: { key: string; operator: string; value: string | string[] }[];
    condition?: ConditionGroup;
    clearCondition?: boolean;
    documentBuilderTemplateIds?: string[];
    includeCompanionCopies?: boolean;
    sortOrder?: number;
  },
): Promise<Attachment | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(attachmentId)) {
      throw new Error("Invalid ID.");
    }
    return await updateAttachmentApi(eventId, attachmentId, data);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteAttachment(
  eventId: string,
  attachmentId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(attachmentId)) {
      throw new Error("Invalid ID.");
    }
    return await deleteAttachmentApi(eventId, attachmentId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

// FA17 — client-side cap matches the audit's recommended backend
// cap (BA11). Prevents a buggy caller from accidentally fanning out
// thousands of S3 deletes (compounds with BA11 which is still open
// server-side; the cap is the first line of defense).
//
// NB: NOT an `export const` — "use server" files restrict exports to
// async functions. The constant lives inline; if a client component
// needs the value, inline it alongside. Keep in sync with the
// backend cap if it lands.
const MAX_BULK_DELETE_ATTACHMENTS = 100;

export async function bulkDeleteAttachments(
  eventId: string,
  attachmentIds: string[],
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    if (attachmentIds.length > MAX_BULK_DELETE_ATTACHMENTS) {
      throw new Error(`tooManyIds: max ${MAX_BULK_DELETE_ATTACHMENTS}`);
    }
    for (const id of attachmentIds) {
      if (!isValidObjectId(id)) {
        throw new Error("Invalid attachment ID.");
      }
    }
    return await bulkDeleteAttachmentsApi(eventId, attachmentIds);
  } catch (error) {
    return handleErrorServer(error);
  }
}

// ── File Management ──

export async function presignFileUpload(
  eventId: string,
  attachmentId: string,
  data: { fileName: string; contentType: string; fileSize: number; guestId?: string },
): Promise<{ uploadUrl: string; s3Key: string } | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(attachmentId)) {
      throw new Error("Invalid ID.");
    }
    return await presignFileUploadApi(eventId, attachmentId, data);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function confirmFileUpload(
  eventId: string,
  attachmentId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string; guestId?: string },
): Promise<AttachmentFile | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(attachmentId)) {
      throw new Error("Invalid ID.");
    }
    return await confirmFileUploadApi(eventId, attachmentId, data);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteFile(
  eventId: string,
  attachmentId: string,
  fileId: string,
): Promise<void | ServerError> {
  try {
    if (
      !isValidObjectId(eventId) ||
      !isValidObjectId(attachmentId) ||
      !isValidObjectId(fileId)
    ) {
      throw new Error("Invalid ID.");
    }
    return await deleteFileApi(eventId, attachmentId, fileId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getDownloadUrl(
  eventId: string,
  attachmentId: string,
  fileId: string,
): Promise<{ url: string } | ServerError> {
  try {
    if (
      !isValidObjectId(eventId) ||
      !isValidObjectId(attachmentId) ||
      !isValidObjectId(fileId)
    ) {
      throw new Error("Invalid ID.");
    }
    return await getDownloadUrlApi(eventId, attachmentId, fileId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

// ── Guest Integration ──

export async function resolveForGuests(
  eventId: string,
  guestIds: string[],
): Promise<GuestAttachmentMap | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await resolveForGuestsApi(eventId, guestIds);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getGuestAttachments(
  eventId: string,
  guestId: string,
): Promise<Attachment[] | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(guestId)) {
      throw new Error("Invalid ID.");
    }
    return await getGuestAttachmentsApi(eventId, guestId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** Warn-only preflight for the send modal: which attachments would NOT reach
 *  this guest's email, and why. Callers must fail soft — a preflight error
 *  must never block or degrade the send flow. */
export async function emailPreflight(
  eventId: string,
  emailTemplateId: string,
  guestId: string,
): Promise<EmailPreflightResult | ServerError> {
  try {
    if (
      !isValidObjectId(eventId) ||
      !isValidObjectId(emailTemplateId) ||
      !isValidObjectId(guestId)
    ) {
      throw new Error("Invalid ID.");
    }
    return await emailPreflightApi(eventId, { emailTemplateId, guestId });
  } catch (error) {
    return handleErrorServer(error);
  }
}

// ── Portal Config ──

export async function getPortalConfigHandling(
  eventId: string,
): Promise<PortalConfig | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await getPortalConfigApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updatePortalConfigHandling(
  eventId: string,
  config: Partial<PortalConfig>,
  /** Art. 50 KI-VO (plan D5): forwarded as the write-only body field. */
  aiContentTool?: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await updatePortalConfigApi(eventId, config, aiContentTool);
  } catch (error) {
    return handleErrorServer(error);
  }
}

// ── Portal (admin-side only — guest endpoints moved to backend-portal) ──

export async function generatePortalLink(
  eventId: string,
  guestId: string,
  attachmentIds?: string[],
  expiresInDays?: number,
): Promise<GeneratedPortalLink | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(guestId)) {
      throw new Error("Invalid ID.");
    }
    return await generatePortalLinkApi(
      eventId,
      { guestId, attachmentIds, expiresInDays },
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * UXA3 (PR17) — admin "Shared Links" tab data source. Returns the
 * full list of portal tokens for the event (live + revoked); the
 * frontend derives status pills from revokedAt + expiresAt.
 */
export async function listPortalTokens(
  eventId: string,
): Promise<PortalTokenSummary[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid event ID.");
    return await listPortalTokensApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** UXA3 (PR17) — single-token revoke (admin action on the table). */
export async function revokePortalToken(
  eventId: string,
  jti: string,
): Promise<{ revoked: boolean } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid event ID.");
    if (!jti) throw new Error("Invalid jti.");
    return await revokePortalTokenApi(eventId, jti);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** UXA3 (PR17) — bulk-revoke for incident response; `source` scopes it
 *  to one origin (EXPORT after a leaked file, manual and mail links stay). */
export async function revokeAllPortalTokens(
  eventId: string,
  source?: PortalTokenSource,
): Promise<{ revokedCount: number; source: PortalTokenSource | null } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid event ID.");
    return await revokeAllPortalTokensApi(eventId, source);
  } catch (error) {
    return handleErrorServer(error);
  }
}
